import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SectionLoader } from "@/components/common";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { initials } from "@/lib/format";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, Legend,
} from "recharts";
import {
  Users, ShieldCheck, CalendarDays, Image as ImageIcon, Ticket, Briefcase,
  Trophy, UserPlus, Flame, QrCode, ArrowRight,
} from "lucide-react";

const CHART_COLORS = ["hsl(var(--chart-1))", "hsl(var(--chart-3))", "hsl(var(--chart-2))", "hsl(var(--chart-5))", "hsl(var(--chart-4))"];
const TOOLTIP_STYLE = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 };
const AXIS_TICK = { fill: "hsl(var(--muted-foreground))", fontSize: 12 };

function StatCard({ icon: Icon, label, value, accent = "bg-primary/10 text-primary" }) {
  return (
    <Card className="border-border bg-card"><CardContent className="p-5">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{label}</p>
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent}`}><Icon className="h-4 w-4" /></div>
      </div>
      <p className="mt-2 font-serif text-3xl font-semibold text-foreground tabular-nums">{value}</p>
    </CardContent></Card>
  );
}

function ChartCard({ title, subtitle, children, testId }) {
  return (
    <Card className="border-border bg-card"><CardContent className="p-6">
      <h2 className="font-serif text-lg font-semibold text-foreground">{title}</h2>
      {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
      <div className="mt-4 h-64" data-testid={testId}>{children}</div>
    </CardContent></Card>
  );
}

export default function AdminAnalytics() {
  const [stats, setStats] = useState(null);
  const [g, setG] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/stats").then((r) => r.data).catch(() => ({})),
      api.get("/gamify/analytics").then((r) => r.data).catch(() => null),
    ]).then(([s, ga]) => { setStats(s); setG(ga); });
  }, []);

  if (!stats) return <SectionLoader />;

  const communityData = [
    { name: "Members", value: stats.total_members || 0 },
    { name: "Events", value: stats.total_events || 0 },
    { name: "Media", value: stats.total_media || 0 },
    { name: "Invites", value: stats.total_invites || 0 },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Analytics</h1>
        <p className="mt-1 text-muted-foreground">A snapshot of your community's activity and engagement.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Users} label="Total members" value={stats.total_members || 0} />
        <StatCard icon={ShieldCheck} label="Core leadership" value={stats.core_members || 0} accent="bg-[rgba(201,119,46,0.2)] text-[#9A531F]" />
        <StatCard icon={CalendarDays} label="Events published" value={stats.published_events || 0} />
        <StatCard icon={ImageIcon} label="Published media" value={stats.total_media || 0} />
        <StatCard icon={Briefcase} label="Business profiles" value={stats.business_profiles || 0} />
        <StatCard icon={QrCode} label="Invites created" value={stats.total_invites || 0} />
        <StatCard icon={UserPlus} label="Referred signups" value={stats.referred_signups || 0} accent="bg-[rgba(201,119,46,0.2)] text-[#9A531F]" />
      </div>

      <ChartCard title="Community at a glance" testId="admin-analytics-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={communityData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="name" tick={AXIS_TICK} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "hsl(var(--secondary))" }} contentStyle={TOOLTIP_STYLE} />
            <Bar dataKey="value" radius={[8, 8, 0, 0]}>{communityData.map((entry, i) => <Cell key={entry.name ?? i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      {g && (
        <>
          <div className="flex items-center gap-2 pt-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            <h2 className="font-serif text-xl font-semibold text-foreground">Engagement &amp; Rewards</h2>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard icon={Trophy} label="Points awarded" value={g.total_points_awarded || 0} />
            <StatCard icon={Flame} label="Activity events" value={g.total_point_events || 0} />
            <StatCard icon={UserPlus} label="Total referrals" value={g.referrals?.total_referred || 0} accent="bg-[rgba(201,119,46,0.2)] text-[#9A531F]" />
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard title="Weekly activity" subtitle="Signups and active members over the last 8 weeks" testId="analytics-weekly-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={g.weekly} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="week" tick={AXIS_TICK} axisLine={false} tickLine={false} tickFormatter={(w) => `W${w}`} />
                  <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="active" name="Active members" stroke="hsl(var(--chart-1))" strokeWidth={2.5} dot={false} />
                  <Line type="monotone" dataKey="signups" name="New signups" stroke="hsl(var(--chart-2))" strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Points by activity" subtitle="Where engagement is coming from" testId="analytics-points-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={g.points_by_type} layout="vertical" margin={{ top: 4, right: 8, left: 40, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="label" width={120} tick={{ ...AXIS_TICK, fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "hsl(var(--secondary))" }} contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="points" radius={[0, 8, 8, 0]}>{(g.points_by_type || []).map((entry, i) => <Cell key={entry.label ?? i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Members by level" testId="analytics-levels-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={g.level_distribution} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="level" tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "hsl(var(--secondary))" }} contentStyle={TOOLTIP_STYLE} />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>{(g.level_distribution || []).map((entry, i) => <Cell key={entry.level ?? i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <Card className="border-border bg-card"><CardContent className="p-6">
              <h2 className="font-serif text-lg font-semibold text-foreground">Top members</h2>
              <div className="mt-3 space-y-1" data-testid="analytics-top-members">
                {(g.top_members || []).slice(0, 6).map((m, i) => (
                  <div key={m.user_id} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-secondary/50">
                    <span className="w-5 text-center text-sm font-semibold tabular-nums text-muted-foreground">{i + 1}</span>
                    <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary text-xs text-primary-foreground">{initials(m.name)}</AvatarFallback></Avatar>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{m.name}</p>
                      <Badge variant="secondary" className="text-[11px]">{m.level_name}</Badge>
                    </div>
                    <span className="font-serif text-base font-semibold tabular-nums text-foreground">{m.points}</span>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          </div>

          <Card className="border-border bg-card"><CardContent className="p-6">
            <h2 className="font-serif text-lg font-semibold text-foreground">Referral funnel</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <div className="rounded-xl border border-border bg-secondary/40 p-4">
                <p className="text-xs text-muted-foreground">Via Core invite QR</p>
                <p className="mt-1 font-serif text-2xl font-semibold tabular-nums text-foreground">{g.referrals?.via_invite || 0}</p>
              </div>
              <div className="rounded-xl border border-border bg-secondary/40 p-4">
                <p className="text-xs text-muted-foreground">Via member card</p>
                <p className="mt-1 font-serif text-2xl font-semibold tabular-nums text-foreground">{g.referrals?.via_card || 0}</p>
              </div>
              <div className="rounded-xl border border-border bg-secondary/40 p-4">
                <p className="text-xs text-muted-foreground">Total referred</p>
                <p className="mt-1 font-serif text-2xl font-semibold tabular-nums text-foreground">{g.referrals?.total_referred || 0}</p>
              </div>
            </div>
            {(g.referrals?.top_referrers || []).length > 0 ? (
              <div className="mt-5">
                <p className="text-sm font-medium text-foreground">Top referrers</p>
                <div className="mt-2 space-y-1" data-testid="analytics-top-referrers">
                  {g.referrals.top_referrers.map((r) => (
                    <div key={r.user_id} className="flex items-center justify-between rounded-lg px-2 py-2 hover:bg-secondary/50">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-7 w-7"><AvatarFallback className="bg-primary text-xs text-primary-foreground">{initials(r.name)}</AvatarFallback></Avatar>
                        <span className="text-sm text-foreground">{r.name}</span>
                      </div>
                      <Badge variant="secondary" className="tabular-nums">{r.count} referred</Badge>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">No referrals yet. Share invite QRs from the <Link to="/admin/manage/qr" className="font-semibold text-primary hover:underline">QR Codes</Link> page to grow the group. <ArrowRight className="inline h-3.5 w-3.5" /></p>
            )}
          </CardContent></Card>
        </>
      )}
    </div>
  );
}
